import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, Download, MoreHorizontal, RotateCcw,
  X, Check, AlertCircle 
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';

interface Transaction {
  id: string;
  amount: number;
  currency: string;
  payment_type: 'credit' | 'debit';
  remarks: string;
  created_at: string;
  vendor_smart_key: string;
  customer_smart_key: string;
  vendor?: Profile;
  customer?: Profile;
}

interface CustomerProfile extends Profile {
  balance: number;
}

type TimeframeType = 'All' | 'Year' | 'Month' | 'Week' | 'Day';

interface ReversalDetails {
  transactionId: string;
  remarks: string;
}

function CustomerDetails() {
  const navigate = useNavigate();
  const { id: profileSmartKey } = useParams();
  const [timeframe, setTimeframe] = useState<TimeframeType>('Month');
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [currentUser, setCurrentUser] = useState<Profile | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalBalance, setTotalBalance] = useState(0);
  const [reversalDetails, setReversalDetails] = useState<ReversalDetails | null>(null);
  const [processingReversal, setProcessingReversal] = useState(false);
  const [displayedTransactions, setDisplayedTransactions] = useState<Transaction[]>([]);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!profileSmartKey) {
      setError('Invalid profile ID');
      setLoading(false);
      navigate('/home');
      return;
    }
    fetchCurrentUser();
  }, [profileSmartKey, navigate]);

  useEffect(() => {
    if (currentUser && profile) {
      fetchTotalBalance();
      fetchTransactions(currentUser);
    }
  }, [currentUser, profile, timeframe]);

  useEffect(() => {
    setDisplayedTransactions(
      showAllTransactions ? transactions : transactions.slice(0, 5)
    );
  }, [transactions, showAllTransactions]);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const { data: userProfile, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userProfile) throw new Error('لم يتم العثور على الملف الشخصي');

      setCurrentUser(userProfile);
      fetchProfileDetails(userProfile);
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('فشل في تحميل بيانات المستخدم');
      setLoading(false);
    }
  };

  const fetchProfileDetails = async (currentUser: Profile) => {
    try {
      if (!profileSmartKey) {
        throw new Error('Invalid profile ID');
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('smart_key', profileSmartKey)
        .single();

      if (profileError) throw profileError;
      if (!profileData) throw new Error('Profile not found');

      const { data: transactionsData, error: transactionsError } = await supabase
        .from('transactions')
        .select('amount')
        .or(`vendor_smart_key.eq.${profileSmartKey},customer_smart_key.eq.${profileSmartKey}`);

      if (transactionsError) throw transactionsError;

      const balance = transactionsData?.reduce((sum, tx) => {
        if (currentUser.account_type === 'customer') {
          return sum + (tx.amount * -1);
        }
        return sum + tx.amount;
      }, 0) || 0;

      setProfile({ ...profileData, balance });
    } catch (err) {
      console.error('Error fetching profile details:', err);
      setError('فشل في تحميل تفاصيل الملف الشخصي');
      navigate('/home');
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalBalance = async () => {
    try {
      if (!profile?.smart_key || !currentUser?.smart_key) return;

      const { data, error } = await supabase
        .from('transactions')
        .select('amount, payment_type')
        .or(
          currentUser.account_type === 'customer'
            ? `and(vendor_smart_key.eq.${profile.smart_key},customer_smart_key.eq.${currentUser.smart_key})`
            : `and(vendor_smart_key.eq.${currentUser.smart_key},customer_smart_key.eq.${profile.smart_key})`
        );

      if (error) throw error;

      const total = (data || []).reduce((sum, transaction) => {
        const amount = Math.abs(transaction.amount);
        if (currentUser.account_type === 'customer') {
          return transaction.payment_type === 'debit' 
            ? sum + amount 
            : sum - amount;
        } else {
          return transaction.payment_type === 'debit'
            ? sum + amount
            : sum - amount;
        }
      }, 0);

      setTotalBalance(total);
    } catch (err) {
      console.error('Error fetching total balance:', err);
    }
  };

  const fetchTransactions = async (currentUser: Profile) => {
    try {
      if (!profile?.smart_key) return;

      setLoading(true);

      let startDate = new Date();
      let query = supabase
        .from('transactions')
        .select(`
          *,
          customer:profiles!transactions_customer_smart_key_fkey(
            username,
            avatar_url
          ),
          vendor:profiles!transactions_vendor_smart_key_fkey(
            username,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false });

      if (timeframe !== 'All') {
        switch (timeframe) {
          case 'Day':
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'Week':
            startDate.setDate(startDate.getDate() - 7);
            break;
          case 'Month':
            startDate.setDate(startDate.getDate() - 30);
            break;
          case 'Year':
            startDate.setDate(startDate.getDate() - 360);
            break;
        }
        query = query.gte('created_at', startDate.toISOString());
      }

      if (currentUser.account_type === 'shop_owner') {
        query = query
          .eq('vendor_smart_key', currentUser.smart_key)
          .eq('customer_smart_key', profileSmartKey);
      } else {
        query = query
          .eq('customer_smart_key', currentUser.smart_key)
          .eq('vendor_smart_key', profileSmartKey);
      }

      const { data, error } = await query;

      if (error) throw error;

      setTransactions(data || []);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('فشل في تحميل المعاملات');
    } finally {
      setLoading(false);
    }
  };

  const handleReverseTransaction = async () => {
    if (!reversalDetails || !currentUser || processingReversal) return;

    setProcessingReversal(true);
    try {
      const originalTransaction = transactions.find(t => t.id === reversalDetails.transactionId);
      if (!originalTransaction) throw new Error('Transaction not found');

      const { error: transactionError } = await supabase
        .from('transactions')
        .insert({
          vendor_smart_key: originalTransaction.vendor_smart_key,
          customer_smart_key: originalTransaction.customer_smart_key,
          amount: originalTransaction.payment_type === 'debit' 
            ? Math.abs(originalTransaction.amount)
            : -Math.abs(originalTransaction.amount),
          currency: originalTransaction.currency,
          payment_type: originalTransaction.payment_type === 'debit' ? 'credit' : 'debit',
          remarks: `إلغاء${reversalDetails.remarks ? ` - ${reversalDetails.remarks}` : ''}`
        });

      if (transactionError) throw transactionError;

      await fetchTransactions(currentUser);
      await fetchTotalBalance();
      setReversalDetails(null);
    } catch (err) {
      console.error('Error reversing transaction:', err);
      setError(err instanceof Error ? err.message : 'Failed to reverse transaction');
    } finally {
      setProcessingReversal(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount));
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('ar-EG', {
      month: 'short',
      day: 'numeric',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const calculateTotals = () => {
    return transactions.reduce(
      (acc, transaction) => {
        if (transaction.payment_type === 'debit') {
          acc.debit += Math.abs(transaction.amount);
        } else {
          acc.credit += Math.abs(transaction.amount);
        }
        return acc;
      },
      { debit: 0, credit: 0 }
    );
  };

  const handleDirectPrint = () => {
    if (!profile || !currentUser) return;

    setPrinting(true);

    try {
      // For Android compatibility, use a different approach
      const isAndroid = /Android/i.test(navigator.userAgent);
      
      if (isAndroid) {
        // Android-specific print handling
        handleAndroidPrint();
      } else {
        // Desktop/iOS print handling
        handleDesktopPrint();
      }
    } catch (error) {
      console.error('Print error:', error);
      setPrinting(false);
    }
  };

  const handleAndroidPrint = () => {
    // Create print content in the same window for Android
    const originalContent = document.body.innerHTML;
    const printContent = generatePrintContent();
    
    // Replace body content temporarily
    document.body.innerHTML = printContent;
    
    // Add print styles to head
    const printStyles = document.createElement('style');
    printStyles.innerHTML = getPrintStyles();
    document.head.appendChild(printStyles);
    
    // Trigger print
    setTimeout(() => {
      window.print();
      
      // Restore original content after print
      setTimeout(() => {
        document.body.innerHTML = originalContent;
        document.head.removeChild(printStyles);
        setPrinting(false);
        
        // Re-initialize React after content restoration
        window.location.reload();
      }, 1000);
    }, 500);
  };

  const handleDesktopPrint = () => {
    // Create a new window for printing (desktop/iOS)
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setPrinting(false);
      return;
    }

    const totals = calculateTotals();
    const transactionsWithBalance = calculateRunningBalance();

    const fullPrintContent = `
      <!DOCTYPE html>
      <html lang="ar" dir="rtl">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>كشف حساب - ${profile.username}</title>
        <style>${getPrintStyles()}</style>
      </head>
      <body>
        ${generatePrintContent()}
      </body>
      </html>
    `;

    // Write content to the new window and trigger print
    printWindow.document.write(fullPrintContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        setPrinting(false);
      }, 500);
    };
  };

  // Get the period label in Arabic
  const getPeriodLabel = () => {
    switch (timeframe) {
      case 'Day': return 'اليوم';
      case 'Week': return 'الأسبوع';
      case 'Month': return 'الشهر';
      case 'Year': return 'السنة';
      case 'All': return 'جميع الفترات';
      default: return 'الفترة المحددة';
    }
  };

  const getPrintStyles = () => {
    return `
      @page {
        size: A4;
        margin: 20mm;
      }
      
      body {
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
        line-height: 1.4;
        color: #000;
        margin: 0;
        padding: 0;
        background: white;
        direction: rtl;
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
      }
      
      .header {
        text-align: center;
        margin-bottom: 2rem;
      }
      
      .header h1 {
        font-size: 1.875rem;
        font-weight: 700;
        color: #1f2937;
        margin-bottom: 0.5rem;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .header-line {
        width: 6rem;
        height: 0.25rem;
        background-color: #f87171;
        margin: 0 auto;
        border-radius: 0.25rem;
      }
      
      .account-info {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 2rem;
        margin-bottom: 2rem;
      }
      
      .info-item {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid #e5e7eb;
        padding-bottom: 0.5rem;
        margin-bottom: 0.75rem;
      }
      
      .info-label {
        font-weight: 500;
        color: #4b5563;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .info-value {
        font-weight: 600;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .summary {
        background-color: #f9fafb;
        border-radius: 0.75rem;
        padding: 1.5rem;
        margin-bottom: 2rem;
      }
      
      .summary h2 {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 1rem;
        color: #1f2937;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .summary-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 1.5rem;
      }
      
      .summary-item {
        text-align: center;
      }
      
      .summary-item h3 {
        font-size: 0.875rem;
        color: #4b5563;
        margin-bottom: 0.25rem;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .amount {
        font-size: 1.5rem;
        font-weight: 700;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .amount.debit {
        color: #dc2626;
      }
      
      .amount.credit {
        color: #16a34a;
      }
      
      .amount.balance {
        color: #4b5563;
      }
      
      .transactions {
        margin-bottom: 2rem;
      }
      
      .transactions h2 {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 1rem;
        color: #1f2937;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      table {
        width: 100%;
        border-collapse: collapse;
        border: 1px solid #e5e7eb;
        border-radius: 0.75rem;
        overflow: hidden;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      th {
        background-color: #f87171;
        color: white;
        padding: 0.75rem 1rem;
        font-weight: 600;
        text-align: center;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      th:first-child,
      th:nth-child(4) {
        text-align: right;
      }
      
      td {
        padding: 0.75rem 1rem;
        border-bottom: 1px solid #e5e7eb;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      tr:nth-child(even) {
        background-color: #f9fafb;
      }
      
      tr:nth-child(odd) {
        background-color: white;
      }
      
      .debit-amount {
        color: #dc2626;
        font-weight: 600;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .credit-amount {
        color: #16a34a;
        font-weight: 600;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .balance-amount {
        font-weight: 600;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .balance-amount.debit-amount {
        color: #dc2626;
      }
      
      .balance-amount.credit-amount {
        color: #16a34a;
      }
      
      .no-transactions {
        text-align: center;
        padding: 2rem;
        color: #6b7280;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .footer {
        border-top: 1px solid #e5e7eb;
        padding-top: 1.5rem;
        margin-top: 2rem;
        font-size: 0.875rem;
        color: #4b5563;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      .numeric {
        font-variant-numeric: tabular-nums;
        font-family: 'Tajawal', system-ui, -apple-system, sans-serif;
      }
      
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          color-adjust: exact;
          font-family: 'Tajawal', system-ui, -apple-system, sans-serif !important;
        }
        
        .header-line {
          background-color: #f87171 !important;
        }
        
        th {
          background-color: #f87171 !important;
          color: white !important;
          font-family: 'Tajawal', system-ui, -apple-system, sans-serif !important;
        }
        
        .summary {
          background-color: #f9fafb !important;
        }
        
        tr:nth-child(even) {
          background-color: #f9fafb !important;
        }
        
        * {
          font-family: 'Tajawal', system-ui, -apple-system, sans-serif !important;
        }
      }
    `;
  };

  const generatePrintContent = () => {
    const totals = calculateTotals();
    const transactionsWithBalance = calculateRunningBalance();

    return `
      <div class="header">
        <h1>كشف حساب</h1>
        <div class="header-line"></div>
      </div>
      
      <div class="account-info">
        <div>
          <div class="info-item">
            <span class="info-label">التاريخ:</span>
            <span class="info-value">${new Date().toLocaleDateString('ar-EG')}</span>
          </div>
          <div class="info-item">
            <span class="info-label">الفترة:</span>
            <span class="info-value">${
              timeframe === 'Day' ? 'يوم' : 
              timeframe === 'Week' ? 'أسبوع' : 
              timeframe === 'Month' ? 'شهر' : 
              timeframe === 'Year' ? 'سنة' :
              'الكل'
            }</span>
          </div>
        </div>
        <div>
          <div class="info-item">
            <span class="info-label">${currentUser?.account_type === 'shop_owner' ? 'العميل' : 'التاجر'}:</span>
            <span class="info-value">${profile?.username}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Smart Key:</span>
            <span class="info-value">${profile?.smart_key}</span>
          </div>
        </div>
      </div>
      
      <div class="summary">
        <h2>الملخص المالي</h2>
        <div class="summary-grid">
          <div class="summary-item">
            <h3>إجمالي الاجل خلال ${getPeriodLabel()}</h3>
            <div class="amount debit numeric">${formatAmount(totals.debit)} ${profile?.currency}</div>
          </div>
          <div class="summary-item">
            <h3>إجمالي المدفوع خلال ${getPeriodLabel()}</h3>
            <div class="amount credit numeric">${formatAmount(totals.credit)} ${profile?.currency}</div>
          </div>
          <div class="summary-item">
            <h3>الرصيد الحالي المستحق الدفع</h3>
            <div class="amount balance numeric ${totalBalance > 0 ? 'debit' : totalBalance < 0 ? 'credit' : ''}">${formatAmount(Math.abs(totalBalance))} ${profile?.currency}</div>
          </div>
        </div>
      </div>
      
      <div class="transactions">
        <h2>تفاصيل المعاملات</h2>
        ${transactionsWithBalance.length === 0 ? `
          <div class="no-transactions">
            لا توجد معاملات في هذه الفترة
          </div>
        ` : `
          <table>
            <thead>
              <tr>
                <th>التاريخ</th>
                <th>مدين</th>
                <th>دائن</th>
                <th>البيان</th>
                <th>الرصيد</th>
              </tr>
            </thead>
            <tbody>
              ${transactionsWithBalance.map(transaction => `
                <tr>
                  <td style="font-size: 0.875rem;">${formatDate(transaction.created_at)}</td>
                  <td style="text-align: center; font-size: 0.875rem;" class="numeric">
                    ${transaction.payment_type === 'debit' ? 
                      `<span class="debit-amount">${formatAmount(Math.abs(transaction.amount))}</span>` : 
                      '<span style="color: #ccc;">-</span>'
                    }
                  </td>
                  <td style="text-align: center; font-size: 0.875rem;" class="numeric">
                    ${transaction.payment_type === 'credit' ? 
                      `<span class="credit-amount">${formatAmount(Math.abs(transaction.amount))}</span>` : 
                      '<span style="color: #ccc;">-</span>'
                    }
                  </td>
                  <td style="font-size: 0.875rem;">${transaction.remarks || 'بدون ملاحظات'}</td>
                  <td style="text-align: center; font-size: 0.875rem;" class="numeric">
                    <span class="balance-amount ${transaction.balance > 0 ? 'debit-amount' : transaction.balance < 0 ? 'credit-amount' : ''}">${formatAmount(Math.abs(transaction.balance))} ${transaction.currency}</span>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `}
      </div>
      
      <div class="footer">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div>
            <div>تم إنشاؤه في: ${new Date().toLocaleString('ar-EG')}</div>
            <div>بواسطة: تطبيق آجل - Ajel.life</div>
          </div>
        </div>
      </div>
    `;
  };

  const calculateRunningBalance = () => {
    let runningBalance = 0;
    return transactions.map(tx => {
      const amount = tx.amount;
      runningBalance += currentUser?.account_type === 'customer' ? -amount : amount;
      return { ...tx, balance: runningBalance };
    });
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-6 pt-8 flex items-center justify-center">
        <div className="text-gray-500">جاري التحميل...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white px-6 pt-8">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-6 pt-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowRight size={24} className="text-gray-800" />
          </button>
          <div>
            <h1 className="text-xl font-semibold">{profile?.username}</h1>
            <p className="text-sm text-gray-500">
              {currentUser?.account_type === 'shop_owner' ? 'العميل' : 'التاجر'} Smart Key: {profile?.smart_key}
            </p>
          </div>
        </div>
        <button className="text-gray-600">{profile?.currency}</button>
      </div>

      <div className="overflow-x-auto hide-scrollbar">
        <div className="bg-gray-50 p-1 rounded-full flex mb-6 w-max">
          {[
            { value: 'Day', label: 'يوم' },
            { value: 'Week', label: 'أسبوع' },
            { value: 'Month', label: 'شهر' },
            { value: 'Year', label: 'سنة' },
            { value: 'All', label: 'الكل' }
          ].map(({ value, label }) => (
            <button
              key={value}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                timeframe === value 
                  ? 'bg-blue-500 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => {
                setTimeframe(value as TimeframeType);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="bg-blue-50 rounded-xl p-4">
          <p className="text-gray-600 mb-1">
            {currentUser?.account_type === 'customer' ? 'الرصيد المستحق' : 'الرصيد عليه'}
          </p>
          <p className={`text-2xl font-semibold ${totalBalance > 0 ? 'text-red-600' : totalBalance < 0 ? 'text-green-600' : ''}`}>
            {formatAmount(Math.abs(totalBalance))} {profile?.currency}
          </p>
        </div>
        <div className="bg-red-50 rounded-xl p-4">
          <p className="text-gray-600 mb-1">اجمالي الاجل</p>
          <p className="text-2xl font-semibold">
            {formatAmount(totals.debit)} {profile?.currency}
          </p>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">العمليات</h2>
          <button 
            onClick={handleDirectPrint}
            disabled={printing}
            className="px-4 py-2 bg-[#F87171] text-white rounded-lg hover:bg-[#EF4444] transition-colors text-sm flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{printing ? 'جاري التحضير...' : 'طباعة كشف الحساب'}</span>
          </button>
        </div>

        <div className="space-y-4">
          {transactions.length === 0 ? (
            <p className="text-center text-gray-500 py-4">لا توجد عمليات في هذه الفترة</p>
          ) : (
            transactions.map((transaction) => (
              <div key={transaction.id} className="p-4 bg-gray-50 rounded-xl">
                {reversalDetails?.transactionId === transaction.id ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">إلغاء العملية</p>
                        <p className="text-sm text-gray-500">{formatDate(transaction.created_at)}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setReversalDetails(null)}
                          className="p-2 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
                          disabled={processingReversal}
                        >
                          <X size={16} />
                        </button>
                        <button
                          onClick={handleReverseTransaction}
                          className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200"
                          disabled={processingReversal}
                        >
                          <Check size={16} />
                        </button>
                      </div>
                    </div>
                    <div>
                      <input
                        type="text"
                        value={reversalDetails.remarks}
                        onChange={(e) => setReversalDetails({
                          ...reversalDetails,
                          remarks: e.target.value
                        })}
                        placeholder="إضافة ملاحظات (اختياري)"
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {transaction.payment_type === 'debit' ? 'عليه' : 'له'}
                      </p>
                      <p className="text-sm text-gray-500">{formatDate(transaction.created_at)}</p>
                      {transaction.remarks && (
                        <p className="text-sm text-gray-600 mt-1">{transaction.remarks}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-4">
                      <p className={`font-semibold ${
                        currentUser?.account_type === 'customer'
                          ? transaction.payment_type === 'debit' ? 'text-green-600' : 'text-red-600'
                          : transaction.payment_type === 'debit' ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {transaction.payment_type === 'debit' ? '-' : '+'}
                        {formatAmount(Math.abs(transaction.amount))} {transaction.currency}
                      </p>
                      {currentUser?.account_type === 'shop_owner' && (
                        <button
                          onClick={() => setReversalDetails({
                            transactionId: transaction.id,
                            remarks: ''
                          })}
                          className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                          title="إلغاء العملية"
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {error && (
        <div className="fixed bottom-6 left-6 right-6 mx-auto max-w-sm p-4 bg-red-50 rounded-xl flex items-center space-x-2 text-red-600 animate-fade-in">
          <AlertCircle size={20} />
          <p className="text-sm">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="ml-auto p-1 hover:bg-red-100 rounded-lg"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default CustomerDetails;